import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle,
  DownloadSimple,
  EnvelopeSimple,
  ShieldCheck,
  Trash,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import BetaQuickStartDialog from "@/components/BetaQuickStartDialog";
import { api } from "@/lib/api";
import { PRIVACY_CONTACT_EMAIL } from "@/lib/legal";
import { useAuth } from "@/context/AuthContext";

function formatDate(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not available" : date.toLocaleString();
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [resending, setResending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);

  const downloadData = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `callsect-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Your Callsect data has been downloaded.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not download your data.");
    } finally {
      setDownloading(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", {
        email: user?.email,
      });
      toast.success(data?.message || "Verification email sent.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || "Could not send the verification email."
      );
    } finally {
      setResending(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmation !== "DELETE") return;
    setDeleting(true);
    try {
      await api.delete("/account", {
        data: { password, confirmation },
      });
      toast.success("Your account and Callsect data have been deleted.");
      logout();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || "Could not delete your account."
      );
      setDeleting(false);
    }
  };

  const deleteEnabled = password && confirmation === "DELETE" && !deleting;

  return (
    <div className="account-page">
      <Nav />

      <main className="account-shell">
        <AppBreadcrumbs items={[{ label: "Account" }]} />
        <section className="account-hero">
          <div>
            <div className="brand-eyebrow">Account</div>
            <h1>Privacy, access and your data</h1>
            <p>Manage your Callsect account, legal information and personal data in one place.</p>
          </div>
          <span className="account-hero__mark" aria-hidden="true">
            <ShieldCheck size={28} weight="duotone" />
          </span>
        </section>

        <section className="account-panel">
          <div className="account-panel__header">
            <span className="account-panel__icon"><ShieldCheck size={22} weight="duotone" /></span>
            <div>
              <div className="account-section-label">Account overview</div>
              <h2>Your Callsect data</h2>
            </div>
          </div>

          <div className="segmented-divider" />

          <div className="account-info-grid">
            <InfoCard label="Email" value={user?.email || "—"} />
            <InfoCard label="Account type" value={user?.account_type || "beta"} />
            <InfoCard
              label="Email status"
              value={user?.email_verified === false ? "Verification required" : "Verified"}
              icon={
                user?.email_verified === false ? (
                  <WarningCircle size={17} className="text-amber-400" />
                ) : (
                  <CheckCircle size={17} className="text-emerald-400" />
                )
              }
            />
            <InfoCard label="Legal notice accepted" value={formatDate(user?.legal_accepted_at)} />
          </div>

          {user?.email_verified === false && (
            <div className="account-verification">
              <div>
                <EnvelopeSimple size={21} weight="duotone" />
                <span>Verify your email address to keep your account secure.</span>
              </div>
              <button type="button" onClick={resendVerification} disabled={resending}>
                {resending ? "Sending…" : "Resend verification"}
              </button>
            </div>
          )}

          <div className="account-actions">
            <button
              type="button"
              onClick={downloadData}
              disabled={downloading}
              className="account-download-button"
            >
              <DownloadSimple size={18} />
              {downloading ? "Preparing…" : "Download my data"}
            </button>
            <div className="account-legal-links">
              <LegalLink to="/privacy">Privacy Notice</LegalLink>
              <LegalLink to="/terms">Beta Terms</LegalLink>
              <LegalLink to="/data-handling">Data handling</LegalLink>
            </div>
          </div>

          <p className="account-contact">
            Privacy or support request? Email{" "}
            <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
          </p>
        </section>

        {user?.account_type === "beta" && (
          <section className="account-panel account-guide-panel">
            <div className="account-panel__header">
              <span className="account-panel__icon"><BookOpen size={22} weight="duotone" /></span>
              <div>
                <div className="account-section-label">Beta tester guide</div>
                <h2>Quick start and current limits</h2>
              </div>
            </div>
            <p className="account-guide-panel__copy">
              Reopen the introduction shown after your first sign-in, review the suggested testing flow, and check your live Callsect beta allowances.
            </p>
            <button type="button" className="account-guide-button" onClick={() => setGuideOpen(true)}>
              <BookOpen size={18} />
              Open quick-start guide
            </button>
          </section>
        )}

        <section className="account-danger">
          <div className="account-danger__header">
            <span className="account-danger__icon"><Trash size={21} weight="duotone" /></span>
            <div>
              <div className="account-section-label">Danger zone</div>
              <h2>Delete account permanently</h2>
            </div>
          </div>

          <p>
            This permanently deletes your account, calls, transcripts, analyses, CRM notes, relationship timelines
            and Writing DNA. This action cannot be undone. Download your data first if you want to keep a copy.
          </p>

          <div className="account-danger__fields">
            <label>
              <span>Current password</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                maxLength={72}
                autoComplete="current-password"
              />
            </label>
            <label>
              <span>Type DELETE to confirm</span>
              <input
                type="text"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={deleteAccount}
            disabled={!deleteEnabled}
            className="account-delete-button"
          >
            <Trash size={18} />
            {deleting ? "Deleting…" : "Delete my account"}
          </button>
        </section>
      </main>

      <BetaQuickStartDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
      />
    </div>
  );
}

function InfoCard({ label, value, icon }) {
  return (
    <article className="account-info-card">
      <small>{label}</small>
      <strong>
        {icon}
        {value}
      </strong>
    </article>
  );
}

function LegalLink({ to, children }) {
  return <Link to={to}>{children}</Link>;
}
